import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { LoadingButton } from "@mui/lab";
import { object, string, number } from "yup";
import { format as dfFormat } from "date-fns";
import errorResolver from "../../utils/error-resolver";
import { Stack } from "@mui/material";
import gregorian from "react-date-object/calendars/gregorian";
import MuiDatePicker from "../../components/date-picker/DatePicker";
import { useProtectedAxiosInstance } from "../../hooks/axiosInstance";
import { useNotificationContext } from "../../context/notificationContext";
import { FilterOperatorEnum } from "../../services/Schemas/ListQueryString";
import { AccessTokensListRequest } from "../../services/Endpoints/access-tokens/AccessTokensEndpoint";
import {
  WorkspaceListColumnFilterable,
  WorkspaceListRequest,
} from "../../services/Endpoints/workspaces";
import MuiAutoCompleteWraper, {
  Option,
} from "../../components/common/autoComplete/MuiAutoCompleteWrapper";
import { PbxServersListRequest } from "../../services/Endpoints/pbxserver/PbxServerListEndpoint";

interface ICreateRoleFrom {
  onSuccess: () => void;
}

export default function CreateTokenForm({ onSuccess }: ICreateRoleFrom) {
  const [wsQuery, setWsQuery] = useState("");
  const [waiting, setWaiting] = useState({
    submit: false,
    workPlace: false,
    PbxServers: false,
  });
  const [accessToken, setAccessToken] = useState<string>("");
  const [pbxServersOptions, setPbxServersOptions] = useState<Option[]>([]);
  const [workspaceOptions, setWorkspaceOptions] = useState<Option[]>([]);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const { setNotification } = useNotificationContext();
  const protectedInstance = useProtectedAxiosInstance();

  const fetchWorkspaces = async (query = "") => {
    setLoadingWorkspace(true);
    try {
      const sectionRequest = new WorkspaceListRequest(protectedInstance);
      const {
        data: {
          workspaces: { data },
        },
      } = await sectionRequest.send({
        filters: [
          {
            value: query,
            operator: FilterOperatorEnum.CONTAINS,
            column: WorkspaceListColumnFilterable.NAME,
          },
        ],
      });
      const options = data.map(({ name, id }) => ({
        label: name,
        value: id.toString(),
      }));
      setWorkspaceOptions(options);
    } catch (error) {
      errorResolver({ error, setNotification });
    } finally {
      setLoadingWorkspace(false);
    }
  };

  const fetchPbxServers = async () => {
    setWaiting((prev) => ({ ...prev, PbxServers: true }));
    try {
      const PbxSeversRequest = new PbxServersListRequest(protectedInstance);
      const {
        data: { pbx_servers },
      } = await PbxSeversRequest.list();
      const _options = pbx_servers.map(({ caption, id }) => ({
        label: caption,
        value: id.toString(),
      }));
      setPbxServersOptions(_options);
    } catch (error) {
      errorResolver({ error, setNotification });
    } finally {
      setWaiting((prev) => ({ ...prev, PbxServers: false }));
    }
  };

  useEffect(() => {
    fetchWorkspaces("");
    fetchPbxServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginSchema = object({
    expired_at: string().optional(),
    workspace_id: number().optional(),
    pbx_server_id: number().optional(),
  });

  const formik = useFormik({
    initialValues: {
      expired_at: "",
      pbx_server_id: "",
      workspace_id: "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setWaiting((prev) => ({ ...prev, submit: true }));
      try {
        const contactRequest = new AccessTokensListRequest(protectedInstance);
        const resp = await contactRequest.create({
          pbx_server_id: values.pbx_server_id ? Number(values.pbx_server_id) : null,
          expired_at: values.expired_at,
          workspace_id: values.workspace_id
            ? Number(values.workspace_id)
            : null,
        });
        const token = resp && resp.data.access_token;
        setAccessToken(token ? token : "");
        formik.resetForm();
        setNotification({
          type: "success",
          message: "توکن جدید با موفقیت ایجاد شد.",
        });
        onSuccess();
      } catch (error: any) {
        errorResolver({ error, setNotification });
      } finally {
        setWaiting((prev) => ({ ...prev, submit: false }));
      }
    },
    enableReinitialize: false,
  });

  const selectedWorkspace =
    workspaceOptions.find(
      (item) => item.value === String(formik.values.workspace_id)
    ) ?? null;
  const selectedPbxServer =
    pbxServersOptions.find(
      (item) => item.value === String(formik.values.pbx_server_id)
    ) ?? null;

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const timer = setTimeout(() => {
      fetchWorkspaces(wsQuery);
    }, 700);
    return () => clearTimeout(timer);
  }, [wsQuery]);

  const pbxServerDisabled = Boolean(formik.values.workspace_id);
  const workspaceDisabled = Boolean(formik.values.pbx_server_id);

  return (
    <Stack
      mx="auto"
      width="100%"
      gap="20.31px"
      maxWidth="100%"
      component="form"
      onSubmit={formik.handleSubmit}
    >
      <MuiDatePicker
        type="text"
        width="100%"
        label="تاریخ انقضا"
        name="expired_at"
        value={formik.values.expired_at}
        handleChange={(date) => {
          if (!date) {
            formik.setFieldValue("expired_at", "");
            return;
          }
          const g = date.convert(gregorian);
          const jsDate = g.toDate();
          const formatted2 = dfFormat(jsDate, "yyyy-MM-dd HH:mm:ss");
          formik.setFieldValue("expired_at", formatted2);
        }}
      />

      <MuiAutoCompleteWraper
        label={"سرور مرکز تماس"}
        name={"pbx_server_id"}
        value={selectedPbxServer}
        disabled={pbxServerDisabled}
        placeholder={"سرور مرکز تماس را انتخاب کنید"}
        options={
          pbxServersOptions.length > 0
            ? pbxServersOptions
            : [{ value: "", label: "بدون سرور مرکزتماس" }]
        }
        onInputChange={(q) => setWsQuery(q)}
        onChange={(opt) =>
          formik.setFieldValue("pbx_server_id", opt?.value ?? null)
        }
      />

      <MuiAutoCompleteWraper
        filterOptions={true}
        options={workspaceOptions}
        value={selectedWorkspace}
        inputValue={wsQuery}
        onInputChange={(q) => setWsQuery(q)}
        onChange={(opt) =>
          formik.setFieldValue("workspace_id", opt?.value ?? "")
        }
        placeholder="تخته را انتخاب کنید"
        label="تخته"
        loading={loadingWorkspace}
        disabled={workspaceDisabled}
      />

      <LoadingButton
        type="submit"
        variant="confirm"
        loading={waiting.submit}
        sx={{ mt: "10px" }}
      >
        تایید
      </LoadingButton>
    </Stack>
  );
}
